import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { infiniteListPageSize } from "../../../../models/common/webConsts";
import { AppUtils } from "source/framework/appUtils";
import * as documentService from "../../../../models/services/documentService";
import { IListUsingComponentState, List } from "../../../framework/List/List";
import { SaveDataObj } from "source/framework/saveData";
import { DialogSettingsCard } from "source/components/framework/Container/DialogSettingsCard";
import { ActionContainer } from "source/components/framework/ActionContainer";
import { ListSearchFilter } from "source/components/framework/List/ListSearchFilter";
import { ListIconButtonsItem } from "source/components/framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { IKeywordListItem } from "source/models/keyword/keywordListItem";
import { ListLinkValue } from "source/components/framework/List/ListLinkValue";
import { DocumentKeywordDialog } from "./DocumentKeywordDialog";

export interface IDocumentKeywordListProp {
    companyId: string;
}

interface IDocumentKeywordListState extends IListUsingComponentState<IKeywordListItem> {
    // List
    intialized: boolean;
    isLoading: boolean;
    pageSize: number;
    page: number;
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    checkedIds: string[];
}

export class DocumentKeywordList extends React.Component<IDocumentKeywordListProp, IDocumentKeywordListState> {
    constructor(props: IDocumentKeywordListProp) {
        super(props);
        this.state = {
            intialized: false,
            isLoading: false,
            page: 1,
            hasMore: true,
            filter: "",
            pageSize: infiniteListPageSize,
            sortColumn: "code",
            sortOrderIsAsc: true,
            items: [],
            selectedId: null,
            checkedIds: [],
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            detailsItem: null
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setEditDialogData: boolean = true) => {
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => documentService.getKeywordListItems(this.props.companyId, !refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, sortColumn, sortOrderIsAsc), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const state = this.state;
        const items = List.getListItems(state.items, state.selectedId, state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let selectedItem = index > -1 ? items.items[index] : null;
        if (moveNext && selectedItem && index < items.items.length - 1) {
            index = index + 1;
            selectedItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            editDialogId: setEditDialogData ? (moveNext && selectedItem ? selectedItem.id : "") : state.editDialogId,
            editDialogHasPrevious: setEditDialogData ? index > 0 : state.editDialogHasPrevious,
            editDialogHasNext: setEditDialogData ? (index < items.items.length - 1 || result.hasMore) : state.editDialogHasNext,
        });
    };

    refreshList = async (setEducationDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, true, false, setEducationDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: IDocumentKeywordListProp, prevState: IDocumentKeywordListState) {
        const props = this.props;
        const state = this.state;
        const prevCompanyId = prevProps.companyId ? prevProps.companyId : "";
        const companyId = props.companyId ? props.companyId : "";
        if (companyId === prevCompanyId) return;
        this.searchItems(state.pageSize, state.page, state.filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    handleAdd = () => {
        this.setState(List.getShowEditDialogStateChange(this.state, Base.emptyGuid));
    };

    handleOpenLine = (id: string) => {
        this.setState(List.getShowEditDialogStateChange(this.state, id));
    };

    handleEditDialogOk = () => {
        this.refreshList(false);
    };

    handleEditDialogCancel = (refreshList: boolean) => {
        this.setState({
            showEditDialog: false
        });
        if (!refreshList) return;
        this.refreshList(false);
    };

    getRemoveSaveData = (props: IDocumentKeywordListProp, ids: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("companyId", props.companyId);
        result.append("ids", ids);
        return result;
    };

    remove = async (ids: string[]): Promise<ApiSuccess> => {
        const props = this.props;
        if (ids.length < 1) return;
        const saveData = this.getRemoveSaveData(props, ids);
        // Call server
        const result = await AppUtils.callService(() => documentService.removeKeywords(saveData.obj));
        if (!result) return null;
        this.refreshList();
        return result;
    };

    handleRemove = async () => {
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        const selectedItems = state.items.filter(i => selectedIds.indexOf(i.id) > -1);
        if (selectedItems.length < 1) return;
        if (await AppUtils.showConfirmationDialog(selectedItems.length > 1
            ? Translations.DoYouReallyWantToRemoveSelectedKeywords
            : String.format(Translations.DoYouReallyWantToRemoveKeywordParameter, selectedItems[0].code), false) === ConfirmationDialogResult.Yes) {
            // Call server
            this.remove(selectedIds);
        }
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(state.pageSize, state.page + 1, state.filter, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <DialogSettingsCard
                    fitToContainerHeight
                    title={Translations.Keywords}
                    info={Translations.DocumentModuleInfoKeywords}
                    cardContent={
                        <ActionContainer
                            leftItems={
                                <ListSearchFilter
                                    searchFilter={state.filter}
                                    onSearchClick={this.changeFilter}
                                />
                            }
                            rightItems={
                                <ListIconButtonsItem
                                    addTooltip={Translations.AddKeyword}
                                    removeTooltip={Translations.RemoveKeyword}
                                    removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                    onAdd={this.handleAdd}
                                    onRemove={this.handleRemove}
                                />
                            }
                        />
                    }
                    content={
                        <List
                            intialized={state.intialized}
                            isLoading={state.isLoading}
                            hasMore={state.hasMore}
                            multiSelect={true}
                            globalLoadingInUse={true}
                            height="100%"
                            header={{
                                columns: [
                                    { size: 5, column: "code", content: Translations.Name },
                                    { size: 7, column: "comment", content: Translations.Description }
                                ]
                            }}
                            lines={state.items.map((item) => {
                                return {
                                    columns: [
                                        {
                                            size: 5,
                                            content: <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue>
                                        },
                                        { size: 7, content: item.comment }
                                    ],
                                    id: item.id
                                };
                            })}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                            onOpenLine={this.handleOpenLine}
                        />
                    } />
                {(state.showEditDialog && !!state.editDialogId) &&
                    <DocumentKeywordDialog
                        id={state.editDialogId}
                        companyId={props.companyId}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onOk={this.handleEditDialogOk}
                        onCancel={this.handleEditDialogCancel}
                        onRemove={this.remove}
                        onRefreshList={() => this.refreshList(false)}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                    />
                }
            </>
        );
    }
}